import { mergeAttributes, Node } from '@tiptap/core'

export interface InfoBlockOptions {
    HTMLAttributes: Record<string, any>,
    blockType?: string;
}

export interface SetInfoBlockOptions {
    blockType?: string
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        InfoBlock: {
            setInfoBlock: (options: SetInfoBlockOptions) => ReturnType
            toggleInfoBlock: (options?: SetInfoBlockOptions) => ReturnType
            unsetInfoBlock: () => ReturnType
        }
    }
}

export const VzInfoBlock = Node.create<InfoBlockOptions>({
    name: 'InfoBlock',

    addOptions() {
        return {
            HTMLAttributes: {},
            blockType: 'info'
        };
    },
    content: 'block+',
    group: 'block',
    defining: true,

    addAttributes() {
        return {
            blockType: {
                default: null,
                parseHTML: element => element.getAttribute('data-blockType') || 'info',
                renderHTML: attributes => attributes.blockType ? { 'data-blockType': attributes.blockType } : {}
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: `div[data-type="${this.name}"]`
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const bt = HTMLAttributes['data-blockType'] || 'info';
        return [
            'div',
            mergeAttributes(
                {
                    'data-type': this.name,
                    'data-blockType': bt,
                    class: 'vz-info-block' + (bt == 'warn' ? ' vz-ib-warning' : bt == 'alert' ? ' vz-ib-error' : '')
                },
                this.options.HTMLAttributes,
                HTMLAttributes
            ),
            [
                'div',
                { class: 'vz-ib-icon' },
                [
                    'i',
                    { class: bt == 'warn' ? 'vzi-notification' : bt == 'alert' ? 'vzi-warning' : 'vzi-info' }
                ],
            ],
            [
                'div',
                { class: 'vz-ib-content' },
                0
            ]
        ]
    },

    addCommands() {
        return {
            setInfoBlock:
                opts =>
                    ({ commands }) => {
                        return commands.wrapIn(this.name, opts);
                    },
            toggleInfoBlock:
                opts =>
                    ({ editor, commands }) => {
                        const blockType = opts?.blockType || 'info';
                        if (editor.isActive('InfoBlock', { blockType })) {
                            return commands.lift(this.name);
                        }
                        else if (editor.isActive('InfoBlock')) {
                            return commands.updateAttributes('InfoBlock', { blockType });
                        }
                        return commands.wrapIn(this.name, { blockType });
                    },
            unsetInfoBlock:
                () =>
                    ({ commands }) => {
                        return commands.lift(this.name);
                    },
        }
    },

    addKeyboardShortcuts() {
        return {
            'Mod-Shift-Alt-I': () => this.editor.commands.toggleInfoBlock({ blockType: 'info' }),
            'Mod-Shift-Alt-Ш': () => this.editor.commands.toggleInfoBlock({ blockType: 'info' }),
            'Mod-Shift-Alt-W': () => this.editor.commands.toggleInfoBlock({ blockType: 'warn' }),
            'Mod-Shift-Alt-Ц': () => this.editor.commands.toggleInfoBlock({ blockType: 'warn' }),
            'Mod-Shift-Alt-A': () => this.editor.commands.toggleInfoBlock({ blockType: 'alert' }),
            'Mod-Shift-Alt-Ф': () => this.editor.commands.toggleInfoBlock({ blockType: 'alert' }),
        }
    },

});
